/**
 * Speech-recognition safety net. Chrome's Web Speech API keeps transcribing a
 * single continuous utterance even while the interviewer is speaking, so the
 * candidate's first "final" after a long question frequently begins with an
 * acoustic echo of the interviewer's own words (their mic picks the speakers
 * up). The model then looks like it "heard itself" and the stored answer is
 * contaminated with the question text.
 *
 * Given the question text the interviewer actually said and the raw candidate
 * final, this strips the echoed prefix and returns the surviving answer. It
 * returns null when the utterance was (nearly) pure echo and should be dropped
 * rather than counted as an answer.
 */
export function stripEchoedQuestion(candidate: string, aiText: string): string | null {
  const a = normalizeSpeech(aiText).split(' ').filter(Boolean);
  const c = normalizeSpeech(candidate).split(' ').filter(Boolean);
  if (a.length < 6 || c.length < a.length) {
    return candidate;
  }

  let ai = 0;
  let ci = 0;
  let matched = 0;
  let skipped = 0;

  while (ai < a.length && ci < c.length) {
    if (wordSimilarity(a[ai], c[ci]) >= WORD_MATCH_THRESHOLD) {
      matched++;
      ai++;
      ci++;
      skipped = 0;
      continue;
    }
    // Candidate inserted an STT-noise word ("...used to get better I...").
    if (skipped < MAX_INSERTIONS_PER_AI_WORD) {
      skipped++;
      ci++;
      continue;
    }
    // Real divergence — the echo, if any, ends here.
    break;
  }

  // Only strip when a healthy portion of the question was actually echoed.
  const minMatch = Math.max(6, Math.floor(a.length * 0.4));
  if (matched < minMatch) {
    return candidate;
  }

  const answer = c.slice(ci).join(' ').trim();
  if (!answer) {
    return null;
  }
  return answer;
}

const WORD_MATCH_THRESHOLD = 0.55;
const MAX_INSERTIONS_PER_AI_WORD = 2;

function normalizeSpeech(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Dice coefficient over character bigrams — tolerant of STT spelling drift. */
function wordSimilarity(a: string, b: string): number {
  if (a === b) return 1;
  if (!a || !b) return 0;
  const bigrams = (s: string): Set<string> => {
    const set = new Set<string>();
    for (let i = 0; i < s.length - 1; i++) set.add(s.slice(i, i + 2));
    return set;
  };
  const ba = bigrams(a);
  const bb = bigrams(b);
  if (ba.size === 0 && bb.size === 0) return 0;
  let common = 0;
  for (const g of ba) {
    if (bb.has(g)) common++;
  }
  return (2 * common) / (ba.size + bb.size);
}